import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Home, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function AdminSettings() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    logoUrl: '',
    faviconUrl: '',
    siteName: 'B&B MAG'
  });
  const [loading, setLoading] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchSettings();
  }, [token, navigate]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/settings`);
      if (response.data) {
        setSettings(response.data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingLogo(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings({...settings, logoUrl: response.data.url});
      toast.success('Logo uploaded successfully!');
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleFaviconUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingFavicon(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_URL}/api/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setSettings({...settings, faviconUrl: response.data.url});
      toast.success('Favicon uploaded successfully!');
    } catch (error) {
      console.error('Error uploading favicon:', error);
      toast.error('Failed to upload favicon');
    } finally {
      setUploadingFavicon(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.post(`${API_URL}/api/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Settings saved successfully!');
      // Reload page to reflect changes
      window.location.reload();
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };

  const handleMigrateImages = async () => {
    if (!confirm('This will migrate all external images (Unsplash, Pexels, base64) to Cloudflare. This may take a few minutes. Continue?')) {
      return;
    }

    try {
      setMigrating(true);
      toast.info('Starting image migration... This may take a few minutes.');
      
      const response = await axios.post(`${API_URL}/api/admin/migrate-images`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const { migrated, total } = response.data;
        toast.success(
          `Migration complete! ${total} images migrated: ${migrated.articles} articles, ${migrated.videos} videos, ${migrated.covers} covers.`
        );
        if (migrated.errors && migrated.errors.length > 0) {
          toast.warning(`${migrated.errors.length} images failed to migrate. Check console for details.`);
          console.error('Migration errors:', migrated.errors);
        }
      }
    } catch (error) {
      console.error('Error migrating images:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate images');
    } finally {
      setMigrating(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Settings · Admin</title>
      </Helmet>

      <div className="min-h-screen bg-background">
        <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur">
          <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-16 items-center justify-between">
              <h1 className="font-display text-2xl font-bold">Settings</h1>
              <div className="flex items-center gap-2">
                <Button asChild variant="ghost" size="sm">
                  <Link to="/admin">
                    <Home className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </header>

        <main className="container max-w-[800px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-6">
            <h2 className="font-display text-3xl font-bold mb-2">Site Settings</h2>
            <p className="text-muted-foreground">Configure your magazine's appearance and branding</p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : (
            <Card className="p-6 bg-[hsl(var(--surface-2))] border-border space-y-6">
              {/* Logo Upload */}
              <div>
                <Label className="text-base font-semibold mb-4 block">Site Logo</Label>
                <div className="space-y-4">
                  {settings.logoUrl && (
                    <div className="p-4 bg-background rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Current Logo:</p>
                      <img 
                        src={settings.logoUrl} 
                        alt="Current logo" 
                        className="h-12 object-contain"
                        style={{ maxWidth: '200px' }}
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="logoUrl" className="mb-2">Logo URL (or upload below)</Label>
                    <Input
                      id="logoUrl"
                      type="url"
                      value={settings.logoUrl}
                      onChange={(e) => setSettings({...settings, logoUrl: e.target.value})}
                      placeholder="https://example.com/logo.png"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploadingLogo}
                      onClick={() => document.getElementById('logo-upload').click()}
                      className="flex-1"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    </Button>
                    <input
                      id="logo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recommended: PNG or SVG format, transparent background, min 200px width
                  </p>
                </div>
              </div>

              {/* Favicon Upload */}
              <div className="pt-6 border-t border-border">
                <Label className="text-base font-semibold mb-4 block">Favicon</Label>
                <div className="space-y-4">
                  {settings.faviconUrl && (
                    <div className="p-4 bg-background rounded-lg">
                      <p className="text-sm text-muted-foreground mb-2">Current Favicon:</p>
                      <img 
                        src={settings.faviconUrl} 
                        alt="Current favicon" 
                        className="h-8 w-8 object-contain"
                      />
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="faviconUrl" className="mb-2">Favicon URL (or upload below)</Label>
                    <Input
                      id="faviconUrl"
                      type="url"
                      value={settings.faviconUrl}
                      onChange={(e) => setSettings({...settings, faviconUrl: e.target.value})}
                      placeholder="https://example.com/favicon.png"
                    />
                  </div>

                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      disabled={uploadingFavicon}
                      onClick={() => document.getElementById('favicon-upload').click()}
                      className="flex-1"
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {uploadingFavicon ? 'Uploading...' : 'Upload Favicon'}
                    </Button>
                    <input
                      id="favicon-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleFaviconUpload}
                      className="hidden"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Recommended: PNG or ICO format, square image (512x512px or larger)
                  </p>
                </div>
              </div>

              {/* Site Name */}
              <div>
                <Label htmlFor="siteName" className="text-base font-semibold mb-2">Site Name</Label>
                <Input
                  id="siteName"
                  value={settings.siteName}
                  onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                  placeholder="B&B MAG"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Used as fallback when logo is not available
                </p>
              </div>

              {/* Image Migration */}
              <div className="pt-6 border-t border-border">
                <Label className="text-base font-semibold mb-2 block">Cloudflare Image Migration</Label>
                <p className="text-sm text-muted-foreground mb-4">
                  Migrate all existing images (Unsplash, Pexels, base64) to Cloudflare CDN for better performance and reliability.
                </p>
                <Button 
                  onClick={handleMigrateImages} 
                  disabled={migrating}
                  variant="outline"
                  className="w-full"
                >
                  {migrating ? 'Migrating... Please wait' : 'Migrate Images to Cloudflare'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2">
                  ⚠️ This will take a few minutes depending on the number of images. Images already on Cloudflare will be skipped.
                </p>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-border">
                <Button onClick={handleSaveSettings} className="w-full">
                  Save Settings
                </Button>
              </div>
            </Card>
          )}
        </main>
      </div>
    </>
  );
}
